import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  Loader2,
} from "lucide-react";
import type { ContractLifecycleState } from "./LifecycleStateBadge";
import { LIFECYCLE_STATE_LABELS } from "./LifecycleStateBadge";
import { LIFECYCLE_TRANSITION_ACTIONS } from "@/lib/lifecycleTransitions";
import { SendExternalDialog } from "./SendExternalDialog";
import { LogCounterDialog } from "./LogCounterDialog";
import type { Persona } from "@/lib/orgContext";
import { handleApiError } from "@/lib/handleApiError";
import { cn } from "@/lib/utils";

interface LifecycleActionsMenuProps {
  contractId: number;
  lifecycleState: ContractLifecycleState | string;
  isAdmin: boolean;
  personas: Persona[];
  onStateChanged: () => void;
  className?: string;
}

/**
 * Compact lifecycle action menu designed to live in the contract header.
 * Surfaces the primary "next step" as a labeled button and keeps secondary
 * actions in a dropdown so users always see what to do next without scrolling
 * down to the negotiation tab.
 */
export function LifecycleActionsMenu({
  contractId,
  lifecycleState,
  isAdmin,
  personas,
  onStateChanged,
  className,
}: LifecycleActionsMenuProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [pending, setPending] = useState<ContractLifecycleState | null>(null);
  const [sendExternalOpen, setSendExternalOpen] = useState(false);
  const [logCounterOpen, setLogCounterOpen] = useState(false);

  const normalized = (lifecycleState ?? "draft") as ContractLifecycleState;
  const isLegal = personas.includes("Legal");
  const actions = LIFECYCLE_TRANSITION_ACTIONS[normalized] ?? [];

  if (!isAdmin || actions.length === 0) {
    return null;
  }

  const primary = actions.find((a) => a.primary) ?? actions[0];
  const secondary = actions.filter((a) => a !== primary);

  const triggerAction = (action: typeof primary) => {
    if (action.requiresLegal && !isLegal) {
      toast({
        title: "Requires Legal persona",
        description: "Switch to the Legal persona to perform this action.",
        variant: "destructive",
      });
      return;
    }
    if (action.toState === "out_for_negotiation" && normalized !== "out_for_negotiation") {
      setSendExternalOpen(true);
      return;
    }
    if (action.toState === "counter_received") {
      setLogCounterOpen(true);
      return;
    }
    void handleTransition(action.toState);
  };

  const handleTransition = async (toState: ContractLifecycleState) => {
    setPending(toState);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/lifecycle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ toState }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw { status: res.status, data: body };
      }
      toast({
        title: "Status updated",
        description: `Contract moved to "${LIFECYCLE_STATE_LABELS[toState] ?? toState}".`,
      });
      onStateChanged();
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not update status" });
    } finally {
      setPending(null);
    }
  };

  const isPending = pending === primary.toState;
  const PrimaryIcon = primary.icon;

  return (
    <>
      <div
        className={cn("flex items-center", className)}
        data-testid="lifecycle-actions-menu"
      >
        <Button
          variant={primary.variant}
          size="sm"
          disabled={isPending || (primary.requiresLegal && !isLegal)}
          onClick={() => triggerAction(primary)}
          className={cn(
            "rounded-r-none border-r-0",
            primary.variant === "default" && "bg-[#0B1834] hover:bg-[#0B1834]/90",
          )}
          data-testid="button-lifecycle-primary"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <PrimaryIcon className="h-3.5 w-3.5 mr-1.5" />
          )}
          {isPending ? "Updating…" : primary.label}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={primary.variant}
              size="sm"
              disabled={!!pending}
              className={cn(
                "rounded-l-none px-2",
                primary.variant === "default" && "bg-[#0B1834] hover:bg-[#0B1834]/90",
              )}
              aria-label="More lifecycle actions"
              data-testid="button-lifecycle-menu"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Move contract to…
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {actions.map((action) => {
              const Icon = action.icon;
              const disabled = !!pending || (action.requiresLegal && !isLegal);
              return (
                <DropdownMenuItem
                  key={action.toState}
                  disabled={disabled}
                  onSelect={(e) => {
                    e.preventDefault();
                    triggerAction(action);
                  }}
                  className="flex items-start gap-3 py-2 cursor-pointer"
                  data-testid={`menu-lifecycle-${action.toState}`}
                >
                  <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight">{action.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      {action.description}
                    </div>
                    {action.requiresLegal && !isLegal && (
                      <div className="text-xs text-destructive mt-0.5">Requires Legal persona</div>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <SendExternalDialog
        open={sendExternalOpen}
        onOpenChange={setSendExternalOpen}
        contractId={contractId}
        onSuccess={() => {
          setSendExternalOpen(false);
          onStateChanged();
        }}
      />
      <LogCounterDialog
        open={logCounterOpen}
        onOpenChange={setLogCounterOpen}
        contractId={contractId}
        onSuccess={onStateChanged}
      />
    </>
  );
}
